import { Link } from '@tanstack/react-router';
import {
  ArrowRight,
  ChevronRight,
  FileText,
  ListOrdered,
  Search,
  Users,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/Dialog.tsx';
import Toggle from '@/components/ui/Toggle.tsx';
import {
  ScreenTabTileCompactContent,
  screenTabTileAutoGridClass,
  screenTabTileCompactClass,
} from '@/components/ui/ScreenTabTile.tsx';
import useGlobalSearchState from '@/features/dashboard/globalSearch/hooks/useGlobalSearchState.ts';
import {
  BookingHit,
  GlobalSearchEmpty,
  GlobalSearchResultsSkeleton,
  PersonHit,
  TourHit,
} from '@/features/dashboard/globalSearch/components/GlobalSearchHit.tsx';
import { ParticipantStatus } from '@/features/dashboard/participants/types/ParticipantStatus.ts';
import { ClientStatus } from '@/features/dashboard/clients/types/ClientStatus.ts';
import {
  GLOBAL_SEARCH_GROUPS,
  INCLUDE_CANCELLED_LABEL,
  OPEN_LIST_LABEL,
  listLink,
  waitlistHitLink,
} from '@/features/dashboard/globalSearch/globalSearchGroups.ts';

const PLACEHOLDER = 'Szukaj sekcji, wyjazdu, osoby, rezerwacji…';

/** Pole w arkuszu menu — wygląda jak wyszukiwarka, otwiera pełny ekran. */
export const MobileGlobalSearchField = ({ onOpen }: { onOpen: () => void }) => (
  <button
    type="button"
    onClick={onOpen}
    className="flex h-16 w-full items-center gap-2.5 rounded-xl border border-neutral-200 bg-neutral-50 px-4 text-left text-[15px] text-neutral-500"
  >
    <Search aria-hidden className="size-5 shrink-0" />
    <span className="truncate">{PLACEHOLDER}</span>
  </button>
);

const headingClass =
  'text-[11px] font-bold uppercase tracking-[0.09em] text-neutral-500';
const rowClass =
  'flex items-center gap-3 border-b border-neutral-100 py-3 text-sm text-base-text no-underline';

interface MobileGlobalSearchProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Wyszukiwarka na telefonie: pełny ekran z polem u góry i „Anuluj”, poniżej
 * kafle pasujących sekcji i listy rekordów. Ten sam stan co okno na szerokim
 * ekranie, inny gospodarz.
 */
const MobileGlobalSearch = ({
  open,
  onOpenChange,
}: MobileGlobalSearchProps) => {
  const {
    term,
    setTerm,
    includeCancelled,
    setIncludeCancelled,
    sections,
    results,
    waiting,
    noResults,
    reset,
  } = useGlobalSearchState();

  const close = () => {
    onOpenChange(false);
    reset();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
    >
      <DialogContent
        aria-describedby={undefined}
        className="left-0 top-0 flex h-[100dvh] max-h-none w-screen max-w-none translate-x-0 flex-col gap-0 rounded-none p-0"
      >
        <div className="flex items-center gap-2 px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
          <label className="flex h-11 flex-1 items-center gap-2.5 rounded-[11px] border border-neutral-200 bg-basic-white px-3 focus-within:border-accent-600 focus-within:ring-4 focus-within:ring-accent-600/15">
            <Search aria-hidden className="size-5 shrink-0 text-accent-900" />
            <input
              type="text"
              enterKeyHint="search"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder={PLACEHOLDER}
              autoComplete="off"
              className="min-w-0 flex-1 bg-transparent font-semibold text-base-text outline-none placeholder:font-normal placeholder:text-neutral-400"
            />
            {term && (
              <button
                type="button"
                aria-label="Wyczyść"
                onClick={reset}
                className="text-neutral-400"
              >
                <X aria-hidden className="size-4" />
              </button>
            )}
          </label>
          <ActionButton tone="ghost" size="sm" onClick={close}>
            Anuluj
          </ActionButton>
        </div>
        <div className="px-4 pb-3">
          <Toggle
            size="sm"
            label={INCLUDE_CANCELLED_LABEL}
            checked={includeCancelled}
            onCheckedChange={setIncludeCancelled}
          />
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
          {noResults && <GlobalSearchEmpty term={term} />}

          {sections.length > 0 && (
            <section>
              <h2 className={cn(headingClass, 'mb-3')}>
                Sekcje ({sections.length})
              </h2>
              <div className={screenTabTileAutoGridClass}>
                {sections.map((item) => (
                  <Link
                    key={item.label}
                    to={item.to}
                    onClick={close}
                    className={screenTabTileCompactClass}
                  >
                    <ScreenTabTileCompactContent
                      icon={item.icon}
                      label={item.label}
                    />
                  </Link>
                ))}
              </div>
            </section>
          )}

          {waiting && <GlobalSearchResultsSkeleton />}

          {results &&
            GLOBAL_SEARCH_GROUPS.filter(
              (group) => results[group.key].length > 0,
            ).map((group) => (
              <section key={group.key}>
                <h2 className={headingClass}>
                  {group.label} ({results[group.key].length})
                </h2>
                {group.key === 'tours' &&
                  results.tours.map((hit) => (
                    <Link
                      key={hit.tour.id}
                      to="/app/tours/$tourId"
                      params={{ tourId: hit.tour.id }}
                      onClick={close}
                      className={rowClass}
                    >
                      <TourHit hit={hit} />
                      <ChevronRight
                        aria-hidden
                        className="size-4 text-neutral-300"
                      />
                    </Link>
                  ))}
                {group.key === 'participants' &&
                  results.participants.map((hit) => (
                    <Link
                      key={hit.participant.id}
                      to="/app/participants/$participantId"
                      params={{ participantId: hit.participant.id }}
                      onClick={close}
                      className={rowClass}
                    >
                      <PersonHit
                        person={hit.participant}
                        tourName={hit.tour?.name}
                        icon={Users}
                        cancelled={
                          hit.participant.status === ParticipantStatus.WITHDRAWN
                        }
                      />
                      <ChevronRight
                        aria-hidden
                        className="size-4 text-neutral-300"
                      />
                    </Link>
                  ))}
                {group.key === 'clients' &&
                  results.clients.map((hit) => (
                    <Link
                      key={hit.client.id}
                      to="/app/clients/$clientId"
                      params={{ clientId: hit.client.id }}
                      onClick={close}
                      className={rowClass}
                    >
                      <PersonHit
                        person={hit.client}
                        tourName={hit.tour?.name}
                        icon={FileText}
                        cancelled={hit.client.status === ClientStatus.CANCELLED}
                      />
                      <ChevronRight
                        aria-hidden
                        className="size-4 text-neutral-300"
                      />
                    </Link>
                  ))}
                {group.key === 'bookings' &&
                  results.bookings.map((hit) => (
                    <Link
                      key={hit.booking.id}
                      to="/app/bookings/$bookingId"
                      params={{ bookingId: hit.booking.id }}
                      onClick={close}
                      className={rowClass}
                    >
                      <BookingHit hit={hit} />
                      <ChevronRight
                        aria-hidden
                        className="size-4 text-neutral-300"
                      />
                    </Link>
                  ))}
                {group.key === 'waitlist' &&
                  results.waitlist.map((hit) => (
                    <Link
                      key={hit.client.id}
                      {...waitlistHitLink(hit)}
                      onClick={close}
                      className={rowClass}
                    >
                      <PersonHit
                        person={hit.client}
                        tourName={hit.tour?.name}
                        icon={ListOrdered}
                      />
                      <ChevronRight
                        aria-hidden
                        className="size-4 text-neutral-300"
                      />
                    </Link>
                  ))}
                <Link
                  {...listLink(group, term, includeCancelled)}
                  onClick={close}
                  className={cn(
                    rowClass,
                    'text-xs font-semibold text-neutral-500',
                  )}
                >
                  <ArrowRight aria-hidden className="size-4" />
                  {OPEN_LIST_LABEL}
                </Link>
              </section>
            ))}
        </div>
        {/* Za treścią, żeby autofokus okna trafił w pole, nie w ukryty „X” tytułu. */}
        <div className="sr-only">
          <DialogTitle>Wyszukiwarka</DialogTitle>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MobileGlobalSearch;
